import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

import { getRegionalBranchForOrdersApi } from './orderManagementUtils';
import { htmlDateToMDY, mdyToHtmlDate } from './orderManagementOrderHelpers';

export function OrderManagementPageHeader({
  filteredCount,
  totalCount,
  deliveryDate,
  onDeliveryDateChange,
  hideDeliveryDatePicker = false,
  fixedDateLabel,
  loading,
  lastRefreshLabel,
  onRefresh,
  colors,
}) {
  return (
    <Box
      sx={{
        maxWidth: 1320,
        mx: 'auto',
        mb: 3,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        px: { xs: 2, sm: 0 },
      }}
    >
      <Box>
        <Typography
          sx={{
            fontFamily: 'Inter Bold, sans-serif',
            fontSize: { xs: 22, sm: 28 },
            fontWeight: 700,
            color: '#1A1D23',
            letterSpacing: '-0.03em',
            lineHeight: 1.15,
          }}
        >
          Order Management
        </Typography>
        <Typography
          sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: '#6B7280', mt: 0.5 }}
        >
          {getRegionalBranchForOrdersApi()} · {filteredCount} of {totalCount} orders
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: colors.white,
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            px: 1.75,
            py: 0.75,
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          }}
        >
          <ReceiptLongOutlinedIcon sx={{ fontSize: 16, color: colors.muted }} />
          <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: colors.muted }}>
            Total
          </Typography>
          <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 15, fontWeight: 700, color: colors.ink }}>
            {totalCount}
          </Typography>
        </Box>

        {hideDeliveryDatePicker ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 38,
              px: 1.75,
              borderRadius: '10px',
              bgcolor: colors.white,
              border: `1px solid ${colors.border}`,
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter Regular, sans-serif',
                fontSize: 13,
                color: colors.ink,
                whiteSpace: 'nowrap',
              }}
            >
              {fixedDateLabel}
            </Typography>
          </Box>
        ) : (
          <TextField
            type='date'
            size='small'
            value={mdyToHtmlDate(deliveryDate)}
            onChange={(e) => {
              if (e.target.value) onDeliveryDateChange(htmlDateToMDY(e.target.value));
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: 38,
                borderRadius: '10px',
                bgcolor: colors.white,
                fontSize: 13,
                fontFamily: 'Inter Regular, sans-serif',
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: '#B0B8CC' },
                '&.Mui-focused fieldset': { borderColor: colors.ink, borderWidth: '1px' },
              },
            }}
          />
        )}

        <Tooltip title={`Last refresh: ${lastRefreshLabel}`} placement='bottom'>
          <span>
            <IconButton
              disabled={loading}
              onClick={onRefresh}
              sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                bgcolor: colors.white,
                border: `1px solid ${colors.border}`,
                color: colors.muted,
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                transition: 'all 0.18s ease',
                '&:hover': {
                  bgcolor: colors.ink,
                  color: colors.white,
                  borderColor: colors.ink,
                  boxShadow: '0 3px 10px rgba(0,0,0,0.18)',
                },
                '&.Mui-disabled': { color: '#D4D4D4', bgcolor: '#FAFAFA' },
              }}
            >
              <RefreshOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
